import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Input } from '@/components/common'
import { ultraadminBillingApi, type BillingPrice, type BillingSettings } from '@/services/api/billing'
import { useToast } from '@/components/ui/ToastProvider'
import { extractErrorMessage } from '@/utils/errorHandler'

type Draft = { unit: string; fx: boolean }

export function UltraadminListPricesPage() {
  const { showToast } = useToast()
  const [settings, setSettings] = useState<BillingSettings | null>(null)
  const [prices, setPrices] = useState<BillingPrice[]>([])
  const [drafts, setDrafts] = useState<Record<string, Draft>>({})
  const [newCcy, setNewCcy] = useState('')
  const [newPrice, setNewPrice] = useState('')
  const [newFx, setNewFx] = useState(false)
  const [saving, setSaving] = useState<string | null>(null)

  const load = () => {
    ultraadminBillingApi.getSettings().then(setSettings).catch((e) => {
      showToast({ message: extractErrorMessage(e, { loadError: 'Einstellungen konnten nicht geladen werden.' }), type: 'error', duration: 4000 })
    })
    ultraadminBillingApi.prices().then((rows) => {
      setPrices(rows)
      setDrafts(Object.fromEntries(rows.map((p) => [p.currency, { unit: String(p.unit_price_gross), fx: !!p.fx_index_enabled }])))
    }).catch(() => undefined)
  }

  useEffect(() => { load() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const savePrice = async (currency: string, unitRaw: string, fx: boolean) => {
    const unit = Number(unitRaw)
    if (!Number.isFinite(unit) || unit < 0) {
      showToast({ message: 'Preis muss eine Zahl ≥ 0 sein.', type: 'error', duration: 4000 })
      return
    }
    setSaving(currency)
    try {
      await ultraadminBillingApi.putPrice(currency.toUpperCase(), unit, fx)
      showToast({ message: 'Preis gespeichert.', type: 'success', duration: 2500 })
      load()
    } catch (e: unknown) {
      showToast({ message: extractErrorMessage(e, { loadError: 'Preis speichern fehlgeschlagen.' }), type: 'error', duration: 5000 })
    } finally {
      setSaving(null)
    }
  }

  const saveRules = async () => {
    if (!settings) return
    setSaving('rules')
    try {
      const saved = await ultraadminBillingApi.putSettings({
        fx_threshold_percent: Number(settings.fx_threshold_percent),
        deal_cap_percent: Number(settings.deal_cap_percent),
      })
      setSettings(saved)
      showToast({ message: 'Regeln gespeichert.', type: 'success', duration: 2500 })
    } catch (e: unknown) {
      showToast({ message: extractErrorMessage(e, { loadError: 'Speichern fehlgeschlagen.' }), type: 'error', duration: 5000 })
    } finally {
      setSaving(null)
    }
  }

  return (
    <div className="p-6 max-w-4xl">
      <h1 className="text-2xl font-bold text-gray-900">Listenpreise</h1>
      <p className="text-sm text-gray-600 mt-1 mb-6">
        Was die Plattform verlangt — ein Sitzpreis für alle Schulrollen, je Währung. Dollar-Kopplung gehört zur Währung, nicht zur Schule.
        Rabatte stehen unter <Link className="text-indigo-700 underline" to="/ultraadmin/school-terms">Schul-Konditionen</Link>.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {prices.map((p) => {
          const draft = drafts[p.currency] || { unit: String(p.unit_price_gross), fx: p.fx_index_enabled }
          return (
            <div key={p.currency} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex justify-between items-center mb-2">
                <h2 className="text-lg font-semibold">{p.currency}</h2>
                <span className={`text-xs px-2 py-0.5 rounded-full ${draft.fx ? 'bg-blue-50 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>
                  {draft.fx ? 'folgt dem Dollar' : 'folgt dem Dollar nicht'}
                </span>
              </div>
              <Input
                label="Brutto / User / Monat"
                value={draft.unit}
                onChange={(e) => setDrafts((d) => ({ ...d, [p.currency]: { ...draft, unit: e.target.value } }))}
              />
              <label className="flex items-center gap-2 text-sm text-gray-700 mt-3">
                <input
                  type="checkbox"
                  checked={draft.fx}
                  onChange={(e) => setDrafts((d) => ({ ...d, [p.currency]: { ...draft, fx: e.target.checked } }))}
                />
                An den Dollar koppeln
              </label>
              <p className="text-xs text-gray-500 mt-2">
                {draft.fx
                  ? 'Wenn diese Währung vs. USD mehr als die Schwelle verliert, steigt der Listenpreis. Nie senken. Schul-Rabatte bleiben Prozent.'
                  : 'Listenpreis bleibt, bis du ihn hier änderst.'}
              </p>
              <Button
                type="button"
                size="sm"
                className="mt-3"
                disabled={saving === p.currency}
                onClick={() => void savePrice(p.currency, draft.unit, draft.fx)}
              >
                Speichern
              </Button>
            </div>
          )
        })}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <h2 className="text-base font-semibold text-gray-900">Weitere Währung</h2>
        <p className="text-sm text-gray-500 mt-1 mb-3">ISO-4217, drei Buchstaben. Wirkt für Schulen mit dieser Währung.</p>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
          <Input label="Währung" value={newCcy} onChange={(e) => setNewCcy(e.target.value.toUpperCase())} />
          <Input label="Brutto / User" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} />
          <label className="flex items-center gap-2 text-sm mb-2">
            <input type="checkbox" checked={newFx} onChange={(e) => setNewFx(e.target.checked)} />
            An den Dollar koppeln
          </label>
          <Button type="button" size="sm" disabled={saving === 'new'} onClick={() => void savePrice(newCcy, newPrice, newFx)}>
            Anlegen
          </Button>
        </div>
      </div>

      {settings && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-base font-semibold text-gray-900">Ab wann passt der Index den Preis an?</h2>
          <p className="text-sm text-gray-500 mt-1 mb-4">
            Globale Schwelle X, nur für gekoppelte Währungen. Deal-Deckel gilt für Partner-Inhaber; Ultraadmin darf 100 %.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              label="FX-Schwelle X (%)"
              type="number"
              value={String(settings.fx_threshold_percent)}
              onChange={(e) => setSettings({ ...settings, fx_threshold_percent: e.target.value })}
            />
            <Input
              label="Deal-Deckel Inhaber (%)"
              type="number"
              value={String(settings.deal_cap_percent)}
              onChange={(e) => setSettings({ ...settings, deal_cap_percent: e.target.value })}
            />
          </div>
          <Button type="button" className="mt-4" disabled={saving === 'rules'} onClick={() => void saveRules()}>
            Regeln speichern
          </Button>
        </div>
      )}
    </div>
  )
}
